import { styled } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import { EDTypography, primary } from "@trailblazer-game/ed-webkit";
import Box from "@mui/material/Box";
import Image from "next/image";
import React, { forwardRef, memo } from "react";
import { theme } from "../../../Theme/Theme";
import { IWithLazyLoadProps, withLazyLoad } from "../../../../hooks/withLazyLoad";
import { useParallax } from "react-scroll-parallax";
import useMediaQuery from "@mui/material/useMediaQuery";

interface IHomeBannerProps extends IWithLazyLoadProps {
  className?: string;
  leftImage: string;
  rightImage: string;
  content: string[];
  title?: string;
  align?: "inherit" | "left" | "center" | "right" | "justify" | undefined;
}

const HomeBanner = memo(forwardRef(({ className, title, content, leftImage, rightImage, align }: IHomeBannerProps, ref) => {
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));
  const { ref: leftImageRef } = useParallax<HTMLDivElement>({
    speed: 10,
    translateY: [ 10, -50, "easeInCirc" ],
    rotateZ: [ 0, 5 ]
  });

  const { ref: rightImageRef } = useParallax<HTMLDivElement>({
    speed: 10,
    translateY: [ 10, -50, "easeInCirc" ],
    rotateZ: [ 0, -5 ]
  });


  return <Stack className={className} direction={isMedium ? "column" : "row"} alignItems={"center"} justifyContent={"center"} spacing={8}
                ref={ref}>
    <Box className={"image"} ref={leftImageRef}>
      <Image src={leftImage} layout={"fill"}/>
    </Box>

    <Stack className={"content"} alignItems={"center"} justifyContent={"center"} spacing={4}>
      {title && <EDTypography variant={"h3"} sx={{ color: primary("main") }} align={align || "center"}>
        {title}
      </EDTypography>}

      <Stack>
        {
          content.map((text, index) => !text ? <br key={index}></br> : <EDTypography variant={"body1"} align={align || "left"} key={index}>
            {text}
          </EDTypography>)
        }
      </Stack>

    </Stack>

    <Box className={"image"} ref={rightImageRef}>
      <Image src={rightImage} layout={"fill"}/>
    </Box>
  </Stack>;
}));

HomeBanner.displayName = "HomeBanner";

const HomeBannerStyled = styled(HomeBanner)(() => ({
  position: "relative",
  zIndex: 10,
  background: "linear-gradient(90deg,rgba(29,28,61,0),rgba(29,28,61,.8) 20%,rgba(29,28,61,.8) 80%,rgba(29,28,61,0))",
  width: "100%",
  maxWidth: "1440px",
  padding: theme.spacing(4, 8),
  ".image": {
    position: "relative",
    display: "inline-flex",
    aspectRatio: "1.25",
    flexGrow: 1,
    height: "100%"
  },
  ".content": {
    maxWidth: "350px",
    flexGrow: 1
  },
  [ theme.breakpoints.down("md") ]: {
    background: "linear-gradient(90deg,rgba(29,28,61,0),rgba(29,28,61,.8) 10%,rgba(29,28,61,.8) 90%,rgba(29,28,61,0))",
    ".image": {
      width: "100%"
    }
  }
}));

// Export
export default withLazyLoad(HomeBannerStyled);